import { useCallback, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import {
  isPerConnectionContactTickets,
  ticketsOnOwnConnections,
} from "../../helpers/contactTicketMode";
import { i18n } from "../../translate/i18n";

/**
 * Abre a conversa de um contato a partir de uma lista (aba Contatos, resultados
 * da busca, grupos em comum).
 *
 * Ordem de resolução — a mesma que a aba Contatos sempre usou:
 *   1. `onSelectContact` (desktop): abre o chat do contato sem criar ticket;
 *   2. ticket ABERTO do próprio usuário  -> navega para ele;
 *   3. ticket AGUARDANDO                 -> aceita e navega;
 *   4. ticket aberto de OUTRO atendente  -> avisa e não navega;
 *   5. nenhum ticket                     -> devolve o contato em `pendingContact`
 *      para o chamador abrir o NewTicketModal.
 *
 * No modo "um por conexão" (unifyTicketsByContact=disabled) os passos 2–4 só
 * olham os chamados das conexões do atendente: o de outra conexão é a conversa
 * de outro número e não é aceito nem bloqueia.
 */
const useOpenContactConversation = (onSelectContact) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);

  const [loadingContactId, setLoadingContactId] = useState(null);
  const [pendingContact, setPendingContact] = useState(null);

  const closeNewTicketModal = useCallback(() => setPendingContact(null), []);

  const openContact = useCallback(
    async (contact) => {
      if (!contact?.id) return;

      if (typeof onSelectContact === "function") {
        onSelectContact(contact);
        return;
      }

      if (loadingContactId) return; // evita múltiplos cliques

      setLoadingContactId(contact.id);

      try {
        const { data } = await api.get("/tickets", {
          params: {
            contactId: contact.id,
            status: "open,pending",
            showAll: "true",
            // Sem queueIds o backend devolve só chamados sem fila — sem isso um
            // chamado já aberto passa despercebido e abre-se outro por engano.
            queueIds: JSON.stringify((user?.queues || []).map((q) => q.id)),
          },
        });

        const tickets = isPerConnectionContactTickets(getSetting)
          ? ticketsOnOwnConnections(data?.tickets, user)
          : data?.tickets || [];

        const myOpenTicket = tickets.find(
          (t) => t.status === "open" && t.userId === user.id
        );

        if (myOpenTicket) {
          history.push(`/tickets/${myOpenTicket.uuid}`);
          return;
        }

        const pendingTicket = tickets.find((t) => t.status === "pending");

        if (pendingTicket) {
          await api.put(`/tickets/${pendingTicket.id}`, {
            status: "open",
            userId: user.id,
          });
          history.push(`/tickets/${pendingTicket.uuid}`);
          return;
        }

        const otherOpenTicket = tickets.find(
          (t) => t.status === "open" && t.userId !== user.id
        );

        if (otherOpenTicket) {
          const attendantName = otherOpenTicket.user?.name || i18n.t("contactDrawer.members.anotherAttendant");
          toast.info(i18n.t("contactDrawer.members.alreadyAttended", { name: attendantName }));
          return;
        }

        // Sem ticket: o chamador abre o modal de novo atendimento.
        setPendingContact(contact);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingContactId(null);
      }
    },
    [getSetting, history, loadingContactId, onSelectContact, user]
  );

  return { openContact, loadingContactId, pendingContact, closeNewTicketModal };
};

export default useOpenContactConversation;
